import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';

interface SpinnerFieldProps {
  variableName: string;
  values: string[];
  defaultValue?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Generic spinner control: up arrow, value display, down arrow.
 * Cycles through values on click; submits via submitUserInput.
 */
const SpinnerField: React.FC<SpinnerFieldProps> = ({
  variableName,
  values,
  defaultValue,
  className,
  ariaLabel
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Optimistic value shown immediately on click; cleared when store confirms or on sync. */
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null);

  const safeValues = useMemo(() => (values?.length > 0 ? values : ['']), [values]);

  const indexFromValue = useCallback(
    (val: string): number => {
      const idx = safeValues.indexOf(val);
      return idx >= 0 ? idx : 0;
    },
    [safeValues]
  );

  const storedIndex = useMemo(() => {
    const stored = (storedValue ?? '').toString().trim();
    if (stored && safeValues.includes(stored)) {
      return indexFromValue(stored);
    }
    if (defaultValue !== undefined && safeValues.includes(defaultValue)) {
      return indexFromValue(defaultValue);
    }
    return 0;
  }, [storedValue, defaultValue, safeValues, indexFromValue]);

  // Clear optimistic when store catches up to the value we submitted
  useEffect(() => {
    if (optimisticValue !== null && (storedValue ?? '').toString().trim() === optimisticValue) {
      setOptimisticValue(null);
    }
  }, [storedValue, optimisticValue]);

  const currentIndex =
    optimisticValue !== null && safeValues.includes(optimisticValue)
      ? indexFromValue(optimisticValue)
      : storedIndex;
  const displayValue = safeValues[currentIndex] ?? '';

  // Sync when stored value changes from broadcast
  useEffect(() => {
    setError(null);
  }, [storedValue]);

  const submitValue = useCallback(
    async (newValue: string) => {
      if (newValue === storedValue) return;

      try {
        setIsSaving(true);
        setError(null);
        await submitUserInput(variableName, newValue);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update value';
        setError(message);
        setOptimisticValue(null);
        uiStore.setError({
          code: 'SPINNER_ERROR',
          message
        });
      } finally {
        setIsSaving(false);
      }
    },
    [variableName, storedValue, submitUserInput, uiStore]
  );

  const goPrevious = useCallback(() => {
    const nextIndex = currentIndex <= 0 ? safeValues.length - 1 : currentIndex - 1;
    const newValue = safeValues[nextIndex] ?? '';
    setOptimisticValue(newValue);
    void submitValue(newValue);
  }, [currentIndex, safeValues, submitValue]);

  const goNext = useCallback(() => {
    const nextIndex = currentIndex >= safeValues.length - 1 ? 0 : currentIndex + 1;
    const newValue = safeValues[nextIndex] ?? '';
    setOptimisticValue(newValue);
    void submitValue(newValue);
  }, [currentIndex, safeValues, submitValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          goPrevious();
          break;
        case 'ArrowDown':
          e.preventDefault();
          goNext();
          break;
        case 'Home':
          e.preventDefault();
          if (safeValues.length > 0 && currentIndex !== 0) {
            const first = safeValues[0] ?? '';
            setOptimisticValue(first);
            void submitValue(first);
          }
          break;
        case 'End':
          e.preventDefault();
          if (safeValues.length > 0 && currentIndex !== safeValues.length - 1) {
            const last = safeValues[safeValues.length - 1] ?? '';
            setOptimisticValue(last);
            void submitValue(last);
          }
          break;
        default:
          break;
      }
    },
    [goPrevious, goNext, safeValues, currentIndex, submitValue]
  );

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-spinner'];
    if (className) classes.push(className);
    if (error) classes.push('rich-text-spinner--error');
    return classes.join(' ');
  }, [className, error]);

  const label = ariaLabel ?? variableName;

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-testid={`spinner-${variableName}`}
    >
      <div
        className="rich-text-spinner__container"
        role="spinbutton"
        aria-label={label}
        aria-valuenow={currentIndex}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, safeValues.length - 1)}
        aria-valuetext={displayValue}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
          onClick={goPrevious}
          disabled={isSaving}
          aria-label={`Previous (${displayValue})`}
          title="Previous value"
        >
          ▲
        </button>

        <span className="rich-text-spinner__value">{displayValue}</span>

        <button
          type="button"
          className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
          onClick={goNext}
          disabled={isSaving}
          aria-label={`Next (${displayValue})`}
          title="Next value"
        >
          ▼
        </button>
      </div>

      {error && (
        <span
          className="rich-text-spinner__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`spinner-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default SpinnerField;
